import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  refineEdge,
  TRIMAP_BACKGROUND,
  TRIMAP_FOREGROUND,
  TRIMAP_UNKNOWN,
  type RefineEdgeOptions,
} from '../../selection/refineEdge';
import { applyRefinedSelection, refineEdgeInputs } from '../../state/selectionActions';
import { Modal, Select, SliderRow } from '../components/controls';

const PREVIEW_MAX = 420;

type View = 'onWhite' | 'onBlack' | 'mask';
type Marker = 'foreground' | 'background' | 'unknown';

const VIEWS: { value: View; label: string }[] = [
  { value: 'onWhite', label: 'On White' },
  { value: 'onBlack', label: 'On Black' },
  { value: 'mask', label: 'Mask' },
];

const MARKERS: { value: Marker; label: string }[] = [
  { value: 'foreground', label: 'Mark Foreground' },
  { value: 'background', label: 'Mark Background' },
  { value: 'unknown', label: 'Mark Undecided' },
];

const MARKER_VALUES: Record<Marker, number> = {
  foreground: TRIMAP_FOREGROUND,
  background: TRIMAP_BACKGROUND,
  unknown: TRIMAP_UNKNOWN,
};

/**
 * A trimap that agrees with the selection: solid areas count as foreground,
 * empty ones as background and the soft edge as undecided. Painting only has to
 * correct where that guess is wrong.
 */
function trimapFromCoverage(coverage: Uint8Array): Uint8Array {
  const trimap = new Uint8Array(coverage.length);
  for (let i = 0; i < coverage.length; i += 1) {
    const value = coverage[i];
    trimap[i] = value >= 250 ? TRIMAP_FOREGROUND : value <= 5 ? TRIMAP_BACKGROUND : TRIMAP_UNKNOWN;
  }
  return trimap;
}

function previewImage(
  pixels: Uint8Array,
  alpha: Uint8Array,
  width: number,
  height: number,
  view: View,
): ImageData {
  const out = new Uint8ClampedArray(width * height * 4);
  const backdrop = view === 'onBlack' ? 0 : 255;
  for (let i = 0; i < alpha.length; i += 1) {
    const o = i * 4;
    if (view === 'mask') {
      out[o] = alpha[i];
      out[o + 1] = alpha[i];
      out[o + 2] = alpha[i];
      out[o + 3] = 255;
      continue;
    }
    // The image's own alpha matters too: a hole in the layer cannot become opaque.
    const a = (alpha[i] / 255) * (pixels[o + 3] / 255);
    out[o] = pixels[o] * a + backdrop * (1 - a);
    out[o + 1] = pixels[o + 1] * a + backdrop * (1 - a);
    out[o + 2] = pixels[o + 2] * a + backdrop * (1 - a);
    out[o + 3] = 255;
  }
  return new ImageData(out, width, height);
}

export function RefineEdgeDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const inputs = useMemo(() => (open ? refineEdgeInputs() : null), [open]);

  const [radius, setRadius] = useState(6);
  const [smooth, setSmooth] = useState(0);
  const [feather, setFeather] = useState(0);
  const [contrast, setContrast] = useState(0);
  const [shiftEdge, setShiftEdge] = useState(0);
  const [view, setView] = useState<View>('onWhite');
  const [marker, setMarker] = useState<Marker>('foreground');
  const [brushSize, setBrushSize] = useState(24);

  const [trimap, setTrimap] = useState<Uint8Array | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  // The dialog body mounts in a portal one commit after this component renders,
  // so the effect has to wait for the canvas to report itself as attached.
  const [attached, setAttached] = useState(false);
  const attach = useCallback((node: HTMLCanvasElement | null) => {
    canvasRef.current = node;
    setAttached(node !== null);
  }, []);
  const paintingRef = useRef(false);

  const options: RefineEdgeOptions = useMemo(
    () => ({ radius, smooth, feather, contrast: contrast / 100, shiftEdge: shiftEdge / 100 }),
    [radius, smooth, feather, contrast, shiftEdge],
  );

  const refined = useMemo(() => {
    if (!inputs) return null;
    return refineEdge(inputs.image, inputs.coverage, trimap, options);
  }, [inputs, options, trimap]);

  const preview = useMemo(() => {
    if (!inputs || !refined) return null;
    return previewImage(
      inputs.image.pixels,
      refined,
      inputs.image.width,
      inputs.image.height,
      view,
    );
  }, [inputs, refined, view]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!attached || !canvas || !preview) return;
    canvas.width = preview.width;
    canvas.height = preview.height;
    canvas.getContext('2d')?.putImageData(preview, 0, 0);
  }, [attached, preview]);

  if (!inputs) {
    return (
      <Modal
        open={open}
        title="Refine Edge"
        onClose={onClose}
        footer={
          <button type="button" className="button button--primary" onClick={onClose}>
            Close
          </button>
        }
      >
        <p className="dialog__note">Select an area first.</p>
      </Modal>
    );
  }

  const { image } = inputs;
  const scale = Math.min(1, PREVIEW_MAX / Math.max(image.width, image.height));

  const paintAt = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const cx = ((event.clientX - rect.left) / rect.width) * image.width;
    const cy = ((event.clientY - rect.top) / rect.height) * image.height;
    // A dab works on a copy: the marks are React state, so they stay immutable.
    const marks = trimap ? Uint8Array.from(trimap) : trimapFromCoverage(inputs.coverage);
    const value = MARKER_VALUES[event.altKey ? 'background' : marker];
    const radiusPx = brushSize / 2;
    const minX = Math.max(0, Math.floor(cx - radiusPx));
    const maxX = Math.min(image.width - 1, Math.ceil(cx + radiusPx));
    const minY = Math.max(0, Math.floor(cy - radiusPx));
    const maxY = Math.min(image.height - 1, Math.ceil(cy + radiusPx));
    for (let y = minY; y <= maxY; y += 1) {
      for (let x = minX; x <= maxX; x += 1) {
        const dx = x + 0.5 - cx;
        const dy = y + 0.5 - cy;
        if (dx * dx + dy * dy <= radiusPx * radiusPx) marks[y * image.width + x] = value;
      }
    }
    setTrimap(marks);
  };

  const apply = () => {
    if (refined) applyRefinedSelection(refined);
    onClose();
  };

  return (
    <Modal
      open={open}
      title="Refine Edge"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="button" onClick={() => setTrimap(null)}>
            Clear marks
          </button>
          <button type="button" className="button" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="button button--primary" onClick={apply}>
            Apply
          </button>
        </>
      }
    >
      <p className="dialog__note">
        Radius re-reads the edge from the image itself. Drag on the preview to mark what the result
        got wrong; Alt marks background.
      </p>

      <div className="colorrange">
        <canvas
          ref={attach}
          className="colorrange__preview"
          style={{ width: image.width * scale, height: image.height * scale }}
          onPointerDown={(event) => {
            paintingRef.current = true;
            event.currentTarget.setPointerCapture(event.pointerId);
            paintAt(event);
          }}
          onPointerMove={(event) => {
            if (paintingRef.current) paintAt(event);
          }}
          onPointerUp={() => {
            paintingRef.current = false;
          }}
        />
      </div>

      <div className="refine__band">
        <span className="field__label">View</span>
        <Select value={view} onChange={setView} options={VIEWS} />
        <span className="field__label">Brush</span>
        <Select value={marker} onChange={setMarker} options={MARKERS} />
      </div>

      <SliderRow label="Brush size" value={brushSize} min={2} max={200} onChange={setBrushSize} />
      <SliderRow label="Radius" value={radius} min={0} max={50} onChange={setRadius} />
      <SliderRow label="Smooth" value={smooth} min={0} max={50} onChange={setSmooth} />
      <SliderRow label="Feather" value={feather} min={0} max={100} onChange={setFeather} />
      <SliderRow label="Contrast" value={contrast} min={-100} max={100} onChange={setContrast} />
      <SliderRow
        label="Shift Edge"
        value={shiftEdge}
        min={-100}
        max={100}
        onChange={setShiftEdge}
      />
    </Modal>
  );
}
